/**
 * Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
 * This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
 * conditions defined in the file COPYING, which is part of this source code package.
 */
import type { components } from 'cmk-shared-typing/typescript/openapi_internal'
import type { Ref } from 'vue'

import type { SimpleIcons } from '@/components/CmkIcon'

import type { DashboardKey } from '@/dashboard/types/dashboard'
import type {
  EffectiveWidgetFilterContext,
  SidebarElementContent,
  StaticTextContent,
  WidgetGeneralSettings
} from '@/dashboard/types/widget'
import { type QuickSetupStageActionIcon } from '@/quick-setup/components/quick-setup/quick_setup_types'

export interface LabelValueItem {
  label: string
  value: string
}

export interface FilterItem extends LabelValueItem {
  name: string
}

export interface ActionButtonIcon extends QuickSetupStageActionIcon {
  name: SimpleIcons
  side: 'left' | 'right'
  rotate?: number | undefined
}

export type MetricType = 'single' | 'combined'

export interface HostServiceContext {
  host?: { host: string }
  service?: { service: string }
}

export interface UseValidate {
  validate: () => boolean
}

export interface UseWidgetHandler extends UseValidate {
  widgetProps: Ref<WidgetProps>
  getSubmitProps: () => Promise<WidgetProps>
}
export interface WidgetProps {
  general_settings: WidgetGeneralSettings
  content: WidgetContentType
  effectiveTitle: string | undefined
  effective_filter_context: EffectiveWidgetFilterContext
}

export enum ElementSelection {
  SPECIFIC = 'SINGLE',
  MULTIPLE = 'MULTI'
}

export interface BaseWidgetProp {
  dashboardKey: DashboardKey
}

export type WidgetFiltersType = {
  [key: string]: {
    [key: string]: string
  }
}

export type WidgetContentType =
  | InventoryContent
  | BarplotContent
  | GaugeContent
  | GraphContent
  | TopListContent
  | ScatterplotContent
  | SingleMetricContent
  | HostStateContent
  | HostStateSummaryContent
  | HostStatisticsContent
  | SiteOverviewContent
  | ServiceStateContent
  | ServiceStateSummaryContent
  | ServiceStatisticsContent
  | CombinedGraphContent
  | PerformanceGraphContent
  | EventStatsContent
  | CustomGraphContent
  | AlertOverviewContent
  | AlertTimelineContent
  | NotificationTimelineContent
  | ProblemGraphContent
  | UserMessagesContent
  | SidebarElementContent
  | URLContent
  | StaticTextContent
  | NtopAlertsContent
  | NtopFlowsContent
  | NtopTopTalkersContent

export type InventoryContent = components['schemas']['InventoryContent']

export type MetricDisplayRangeModel = components['schemas']['MetricDisplayRangeModel']
export type FixedDataRangeModel = components['schemas']['MetricDisplayRangeFixedModel']
export type BarplotContent = components['schemas']['BarplotContent']
export type GaugeContent = components['schemas']['GaugeContent']
export type GraphContent = components['schemas']['SingleTimeseriesContent']
export type SingleMetricContent = components['schemas']['SingleMetricContent']
export type ScatterplotContent = components['schemas']['AverageScatterplotContent']
export type TopListContent = components['schemas']['TopListContent']
export type HostStateContent = components['schemas']['HostStateContent']
export type HostStateSummaryContent = components['schemas']['HostStateSummaryContent']
export type HostStatisticsContent = components['schemas']['HostStatsContent']
export type SiteOverviewContent = components['schemas']['SiteOverviewContent']
export type ServiceStateContent = components['schemas']['ServiceStateContent']
export type ServiceStateSummaryContent = components['schemas']['ServiceStateSummaryContent']
export type ServiceState = components['schemas']['MonitoringState']
export type ServiceStatisticsContent = components['schemas']['ServiceStatsContent']
export type CombinedGraphContent = components['schemas']['CombinedGraphContent']
export type PerformanceGraphContent = components['schemas']['PerformanceGraphContent']
export type EventStatsContent = components['schemas']['EventStatsContent']
export type CustomGraphContent = components['schemas']['CustomGraphContent']
export type AlertOverviewContent = components['schemas']['AlertOverviewContent']
export type AlertTimelineContent = components['schemas']['AlertTimelineContent']
export type NotificationTimelineContent = components['schemas']['NotificationTimelineContent']
export type ProblemGraphContent = components['schemas']['ProblemGraphContent']
export type UserMessagesContent = components['schemas']['UserMessagesContent']
export type URLContent = components['schemas']['URLContent']
export type NtopAlertsContent = components['schemas']['NtopAlertsContent']
export type NtopFlowsContent = components['schemas']['NtopFlowsContent']
export type NtopTopTalkersContent = components['schemas']['NtopTopTalkersContent']

export type TitleSpec = components['schemas']['WidgetTitle']
export type DefaultOrColor = components['schemas']['DefaultOrColor']
export type GraphRenderOptions = components['schemas']['GraphRenderOptions']
export type HostState = components['schemas']['HostState']
export type CombinedGraphContentPresentation =
  components['schemas']['CombinedGraphContent']['presentation']
export type ForStates = components['schemas']['ForStates']
export type ShowServiceStatusType = components['schemas']['MetricStatusDisplayModel']['type']
export type InventoryLinkType = components['schemas']['ApiVisualLink']['type']
export type LinkContentType = components['schemas']['ApiVisualLink']
